import React, { useState } from "react";
import Reveal from "@/components/Reveal";
import VideoModal from "@/components/VideoModal";

const HERO_BG =
  "https://customer-assets.emergentagent.com/job_christopher-magician/artifacts/io1muvmd_SnapInsta.to_655260747_18097379590956766_4754292238018524416_n.jpg";

const SHOWREEL_URL =
  "https://customer-assets.emergentagent.com/job_christopher-magician/artifacts/ullnqmtt_PROMO%20NEW.mp4";

export default function Hero() {
  const [showreelOpen, setShowreelOpen] = useState(false);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden"
    >
      {/* Background */}
      <img
        src={HERO_BG}
        alt="Cinematic stage with a single spotlight"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="vignette" />
      <div className="noise-overlay" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-14 pt-16 md:pt-20 pb-20 md:pb-28">
        <Reveal>
          <h1
            data-testid="hero-headline"
            className="font-display text-[#F5F5F0] text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.95] tracking-tight max-w-5xl"
          >
            Magic that lingers
            <br />
            <span className="font-italic-display text-[#C5A059]">
              long after the curtain.
            </span>
          </h1>
        </Reveal>

        <Reveal delay={2}>
          <p
            data-testid="hero-subtitle"
            className="mt-12 md:mt-14 max-w-xl text-[#A1A1AA] text-base md:text-lg leading-relaxed"
          >
            Sleight of hand, mentalism, and theatre — performed for private
            rooms, intimate galas, and the kind of evenings people don&apos;t
            forget.
          </p>
        </Reveal>

        <Reveal delay={3}>
          <div className="mt-16 md:mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-book-btn"
              onClick={() => scrollTo("booking")}
              className="btn-gold"
            >
              Book the Experience
            </button>
            <button
              data-testid="hero-watch-btn"
              onClick={() => setShowreelOpen(true)}
              className="btn-ghost"
            >
              Watch the Showreel
            </button>
          </div>
        </Reveal>

        {/* Horizontal performance gallery — auto-scrolling marquee */}
        <Reveal delay={3}>
          <div
            data-testid="hero-gallery"
            className="mt-16 -mx-6 md:-mx-10 lg:-mx-14 px-6 md:px-10 lg:px-14 overflow-hidden"
          >
            {(() => {
              const items = [
                {
                  url: "https://customer-assets.emergentagent.com/job_christopher-magician/artifacts/xj5f8a45_1146.jpg",
                  caption: "Theater Performances",
                },
                {
                  url: "https://customer-assets.emergentagent.com/job_christopher-magician/artifacts/yi7udxbz_SnapInsta.to_623657219_18086092772327164_3289484103635287655_n.jpg",
                  caption: "Sold Out Stages",
                },
                {
                  url: "https://customer-assets.emergentagent.com/job_christopher-magician/artifacts/ii13knwh_SnapInsta.to_656654030_18194873419359290_2490623876006828938_n.jpg",
                  caption: "Close-Up Parlour",
                },
                {
                  url: "https://customer-assets.emergentagent.com/job_christopher-magician/artifacts/z68ee3ze_670437033_18154756489455866_4218932713434855993_n.jpg",
                  caption: "Corporate Events",
                },
                {
                  url: "https://customer-assets-eiarnc6j.emergentagent.net/job_christopher-magician/artifacts/hpe4892m_449046162_10159636443516008_3693446352326515308_n.jpg",
                  caption: "Private Events",
                },
              ];
              const loop = [...items, ...items];
              return (
                <div className="flex gap-5 md:gap-6 w-max gallery-marquee">
                  {loop.map((g, i) => (
                    <figure
                      key={i}
                      data-testid={`hero-gallery-item-${i % items.length}`}
                      aria-hidden={i >= items.length ? true : undefined}
                      className="relative shrink-0 w-[78vw] sm:w-[44vw] md:w-[32vw] lg:w-[22vw] aspect-[4/3] group overflow-hidden border border-[#C5A059]/15"
                    >
                      <img
                        src={g.url}
                        alt={g.caption}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
                      <figcaption className="absolute bottom-4 left-5 right-5 flex items-end justify-between gap-3">
                        <span className="font-display text-[#F5F5F0] text-base md:text-lg leading-tight">
                          {g.caption}
                        </span>
                        <span className="overline text-[10px]">
                          0{(i % items.length) + 1}
                        </span>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              );
            })()}
            <div className="mt-3 overline text-[#A1A1AA] text-[10px] hidden md:block">
              Hover to pause
            </div>
          </div>
        </Reveal>

        {/* Stats row */}
        <Reveal delay={4}>
          <div className="mt-20 grid grid-cols-3 gap-6 max-w-2xl mx-auto border-t border-[#C5A059]/15 pt-8">
            {[
              {
                key: "years-performing",
                node: <>20+</>,
                label: "Years Performing",
              },
              {
                key: "of-performances",
                node: (
                  <>
                    100<span className="text-xl md:text-2xl align-baseline">s</span>
                  </>
                ),
                label: "of Performances",
              },
              {
                key: "countries",
                node: <>Multiple</>,
                label: "Countries",
              },
            ].map((s) => (
              <div key={s.key} data-testid={`hero-stat-${s.key}`}>
                <div className="font-display text-[#C5A059] text-3xl md:text-4xl leading-none">
                  {s.node}
                </div>
                <div className="text-[#A1A1AA] text-xs md:text-sm mt-1 tracking-wide">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Venues / Credits marquee */}
        <Reveal delay={4}>
          <div
            data-testid="venue-marquee"
            className="mt-16 border-t border-[#C5A059]/15 pt-8 overflow-hidden"
          >
            <div className="overline text-[10px] text-center text-[#A1A1AA] mb-6">
              Previous Performances
            </div>
            {(() => {
              const venues = [
                {
                  alt: "French Drop Magic & Bar",
                  url: "https://customer-assets-eiarnc6j.emergentagent.net/job_christopher-magician/artifacts/06ed72ddbcb3e846_frenchdroprogo.png",
                },
                {
                  alt: "Poe's Magic Theatre",
                  url: "https://customer-assets-eiarnc6j.emergentagent.net/job_christopher-magician/artifacts/f24f7061395dd5de_730b63_b76bbec06dd74c9b8bdcd810d7d3b6e8~mv2.png",
                },
                {
                  alt: "DCAC",
                  url: "https://customer-assets-eiarnc6j.emergentagent.net/job_christopher-magician/artifacts/dbffc9646cac622c_images%20%281%29.png",
                },
                {
                  alt: "Greenbelt Arts Center",
                  url: "https://customer-assets-eiarnc6j.emergentagent.net/job_christopher-magician/artifacts/48d63e07644bf5ae_images%20%282%29.jfif",
                },
                {
                  alt: "DC Festival of Magic",
                  url: "https://customer-assets-eiarnc6j.emergentagent.net/job_christopher-magician/artifacts/20898aee0a942716_images%20%283%29.jfif",
                },
                {
                  alt: "Right Proper Brewing Company",
                  url: "https://customer-assets-eiarnc6j.emergentagent.net/job_christopher-magician/artifacts/c19622e521183c04_images%20%281%29.jfif",
                },
                {
                  alt: "Give A Hoot Comedy Club",
                  url: "https://customer-assets-eiarnc6j.emergentagent.net/job_christopher-magician/artifacts/617e6c7cced9e168_images.jfif",
                },
                {
                  alt: "Hilton",
                  url: "https://customer-assets-eiarnc6j.emergentagent.net/job_christopher-magician/artifacts/70873923cc05c943_hilton-international-logo-png-transparent.webp",
                },
                {
                  alt: "Filling Station",
                  url: "https://customer-assets-eiarnc6j.emergentagent.net/job_christopher-magician/artifacts/00754115af1ac834_images.png",
                },
                {
                  alt: "HNTB",
                  url: "https://customer-assets-eiarnc6j.emergentagent.net/job_christopher-magician/artifacts/07f94b3f9d5b0128_1650px_HNTB_logo.png",
                },
              ];
              const loop = [...venues, ...venues];
              return (
                <div className="flex items-center gap-10 md:gap-14 w-max gallery-marquee">
                  {loop.map((v, i) => (
                    <div
                      key={i}
                      aria-hidden={i >= venues.length ? true : undefined}
                      className="shrink-0 h-16 md:h-20 w-36 md:w-44 flex items-center justify-center bg-[#F5F5F0] border border-[#C5A059]/30 rounded-sm px-4"
                    >
                      <img
                        src={v.url}
                        alt={v.alt}
                        loading="lazy"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </Reveal>
      </div>
    </section>
    <VideoModal
      open={showreelOpen}
      onClose={() => setShowreelOpen(false)}
      src={SHOWREEL_URL}
    />
    </>
  );
}
